import { useState, useEffect, useCallback, useRef } from 'react';
import { ChatItem } from '../types';
import { getChatList } from '../utils/chatApi';

export const useChatList = () => {
  const [chats, setChats] = useState<ChatItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const isRefreshingRef = useRef(false);
  const [unreadChatIds, setUnreadChatIds] = useState<Set<string>>(new Set());

  const fetchChatList = useCallback(async (limit: number = 20, offset: number = 0) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getChatList(limit, offset);
      setChats(response.chats);
      setTotal(response.total);
      setHasMore(response.chats.length < response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch chat list');
      console.error('Error fetching chat list:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMoreChats = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    setError(null);
    try {
      const response = await getChatList(20, chats.length);
      setChats(prev => [...prev, ...response.chats]);
      setTotal(response.total);
      setHasMore(chats.length + response.chats.length < response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more chats');
      console.error('Error loading more chats:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [chats.length, isLoadingMore, hasMore]);

  const refreshChatList = useCallback(async () => {
    // If we're already refreshing, skip to avoid duplicate requests
    if (isRefreshingRef.current) return;

    isRefreshingRef.current = true;
    setError(null);

    try {
      // Fetch the same number of items we currently have loaded (minimum 20)
      const currentLoadedCount = Math.max(chats.length, 20);
      const response = await getChatList(currentLoadedCount, 0);

      // Update the chat list while maintaining the loaded count
      setChats(response.chats);
      setTotal(response.total);
      setHasMore(response.chats.length < response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh chat list');
      console.error('Error refreshing chat list:', err);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [chats.length]);

  const markChatUnread = useCallback((chatId?: string | null) => {
    if (!chatId) return;
    setUnreadChatIds(prev => {
      if (prev.has(chatId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(chatId);
      return next;
    });
  }, []);

  const markChatRead = useCallback((chatId?: string | null) => {
    if (!chatId) return;
    setUnreadChatIds(prev => {
      if (!prev.has(chatId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(chatId);
      return next;
    });
  }, []);

  useEffect(() => {
    fetchChatList();
  }, [fetchChatList]);

  return {
    chats,
    isLoading,
    isLoadingMore,
    error,
    total,
    hasMore,
    refreshChatList,
    loadMoreChats,
    unreadChatIds,
    markChatUnread,
    markChatRead,
  };
};
